import { ensurePostHogInitialized } from "./analytics";

export type SignupContext = {
  page_path: string;
};

type Journey = { context: SignupContext; startedAt: number; engaged: boolean; completed: boolean };
const STORAGE_KEY = "dash0.signupJourney";
const MAX_AGE = 24 * 60 * 60 * 1000;
let journey: Journey | null = null;

function readJourney(): Journey | null {
  if (!journey) {
    try {
      const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "null");
      if (stored && typeof stored.startedAt === "number" && typeof stored.completed === "boolean" &&
          typeof stored.context?.page_path === "string") {
        journey = {
          startedAt: stored.startedAt,
          completed: stored.completed,
          engaged: stored.engaged === true,
          context: {
            page_path: stored.context.page_path,
          },
        };
      }
    } catch { /* Storage may be unavailable; use in-memory tracking. */ }
  }
  if (journey && Date.now() - journey.startedAt >= MAX_AGE) journey = null;
  return journey;
}

function saveJourney(value: Journey) {
  journey = value;
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch { /* Best effort. */ }
}

function capture(name: string, properties: SignupContext & { auth_method?: string } | { cta_id: string; page: string; placement: string }, userId?: string) {
  // Send immediately: completion is followed by a redirect to app.dash0.com.
  void ensurePostHogInitialized().then((posthog) => {
    // The app also uses Clerk's user ID as its PostHog distinct_id.
    if (userId) posthog.identify(userId);
    posthog.capture(name, properties, {
      send_instantly: true,
      ...(userId ? { transport: "sendBeacon" as const } : {}),
    });
  }).catch(() => { /* Analytics must never interrupt signup. */ });
}

/** Track each CTA click independently of the deduplicated signup journey. */
export function clickSignupCta(placement: string) {
  capture("cta.clicked", { cta_id: "start_free", page: window.location.pathname, placement });
}

/** One journey per tab, retained through reloads, OAuth and Clerk's verification steps. */
export function openSignup(context: SignupContext) {
  const current = readJourney();
  if (current && !current.completed) return;
  saveJourney({ context, startedAt: Date.now(), engaged: false, completed: false });
  capture("signup.opened", context);
}

/** First meaningful interaction inside Clerk; opening or focusing is not engagement. */
export function startSignup() {
  const current = readJourney();
  if (!current || current.engaged || current.completed) return;
  saveJourney({ ...current, engaged: true });
  capture("signup.started", current.context);
}

// Structural type keeps Clerk-specific resources out of the tracking/storage layer.
type Verification = { status: string | null; strategy: string | null };
export function completeSignup(signup: {
  status: string | null;
  createdUserId: string | null;
  verifications: { externalAccount: Verification; emailAddress: Verification };
}) {
  const current = readJourney();
  if (!current || current.completed || signup.status !== "complete" || !signup.createdUserId) return;
  const external = signup.verifications.externalAccount;
  const email = signup.verifications.emailAddress;
  const strategy = external.status === "verified" ? external.strategy :
    email.status === "verified" ? email.strategy : null;
  const method = strategy === "enterprise_sso" ? "sso" :
    strategy?.startsWith("oauth_") ? strategy.slice(6) :
    strategy?.startsWith("email_") ? "email" : undefined;
  saveJourney({ ...current, completed: true });
  capture("signup.completed", { ...current.context, ...(method ? { auth_method: method } : {}) }, signup.createdUserId);
}
